/**
 * IndexedDB persistence for Loro docs (design spec §9.1 "Client"): one record per doc holding the
 * last snapshot and the updates committed locally that the server has not yet acknowledged.
 */
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

interface DrangDB extends DBSchema {
  docs: {
    key: string;
    value: { docId: string; snapshot: Uint8Array | null; savedAt: number; epoch?: number };
  };
  pending: {
    key: number;
    value: { id?: number; docId: string; payload: Uint8Array; createdAt: number };
    indexes: { byDoc: string };
  };
}

let dbPromise: Promise<IDBPDatabase<DrangDB>> | null = null;

function db(): Promise<IDBPDatabase<DrangDB>> {
  if (!dbPromise) {
    dbPromise = openDB<DrangDB>("drang", 1, {
      upgrade(d) {
        d.createObjectStore("docs", { keyPath: "docId" });
        const p = d.createObjectStore("pending", { keyPath: "id", autoIncrement: true });
        p.createIndex("byDoc", "docId");
      },
    });
  }
  return dbPromise;
}

export async function loadSnapshot(docId: string): Promise<{ snapshot: Uint8Array | null; epoch: number }> {
  try {
    const row = await (await db()).get("docs", docId);
    return { snapshot: row?.snapshot ?? null, epoch: row?.epoch ?? 0 };
  } catch {
    return { snapshot: null, epoch: 0 };
  }
}

/** Forget one doc entirely (the server replaced it: spec §9.28 import mode `replace`). */
export async function wipeDoc(docId: string): Promise<void> {
  try {
    const d = await db();
    await d.delete("docs", docId);
    const rows = await d.getAllFromIndex("pending", "byDoc", docId);
    const tx = d.transaction("pending", "readwrite");
    await Promise.all(rows.map((r) => tx.store.delete(r.id!)));
    await tx.done;
  } catch {
    /* ignore */
  }
}

export async function saveSnapshot(docId: string, snapshot: Uint8Array, epoch = 0): Promise<void> {
  try {
    await (await db()).put("docs", { docId, snapshot, savedAt: Date.now(), epoch });
  } catch {
    /* quota or private mode: the doc still lives in memory and on the server */
  }
}

export async function appendPending(docId: string, payload: Uint8Array): Promise<number | null> {
  try {
    return await (await db()).add("pending", { docId, payload, createdAt: Date.now() });
  } catch {
    return null;
  }
}

export async function loadPending(docId: string): Promise<{ id: number; payload: Uint8Array }[]> {
  try {
    const rows = await (await db()).getAllFromIndex("pending", "byDoc", docId);
    return rows.map((r) => ({ id: r.id!, payload: r.payload }));
  } catch {
    return [];
  }
}

export async function clearPending(ids: number[]): Promise<void> {
  if (!ids.length) return;
  try {
    const tx = (await db()).transaction("pending", "readwrite");
    await Promise.all(ids.map((id) => tx.store.delete(id)));
    await tx.done;
  } catch {
    /* ignore */
  }
}

export async function wipeAll(): Promise<void> {
  try {
    const d = await db();
    await d.clear("docs");
    await d.clear("pending");
  } catch {
    /* ignore */
  }
}
